import React from 'react'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md'
  isLoading?: boolean
}

const variantStyles: Record<string, React.CSSProperties> = {
  primary: { background: '#4f46e5', color: '#ffffff', border: '1px solid #4f46e5' },
  secondary: { background: '#ffffff', color: '#64748b', border: '1px solid #e8ecf4' },
  ghost: { background: 'transparent', color: '#64748b', border: '1px solid transparent' },
  danger: { background: '#dc2626', color: '#ffffff', border: '1px solid #dc2626' },
}

const hoverStyles: Record<string, React.CSSProperties> = {
  primary: { background: '#4338ca', border: '1px solid #4338ca' },
  secondary: { background: '#f8fafc' },
  ghost: { background: '#f1f5f9' },
  danger: { background: '#b91c1c', border: '1px solid #b91c1c' },
}

const sizeStyles: Record<string, React.CSSProperties> = {
  sm: { height: 30, padding: '0 12px', fontSize: 12, borderRadius: 6 },
  md: { height: 36, padding: '0 14px', fontSize: 13, borderRadius: 7 },
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled,
  children,
  className = '',
  style,
  onMouseEnter,
  onMouseLeave,
  ...props
}: ButtonProps) {
  const [hovered, setHovered] = React.useState(false)

  const combinedStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 500,
    fontFamily: 'inherit',
    cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
    opacity: disabled || isLoading ? 0.6 : 1,
    transition: 'background 0.15s, border-color 0.15s',
    outline: 'none',
    gap: 6,
    ...variantStyles[variant],
    ...sizeStyles[size],
    ...(hovered && !disabled && !isLoading ? hoverStyles[variant] : {}),
    ...style,
  }

  return (
    <button
      {...props}
      disabled={disabled || isLoading}
      className={className}
      style={combinedStyle}
      onMouseEnter={(e) => { setHovered(true); onMouseEnter?.(e) }}
      onMouseLeave={(e) => { setHovered(false); onMouseLeave?.(e) }}
    >
      {isLoading ? (
        <>
          <svg className="animate-spin" width={14} height={14} viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity={0.25} />
            <path fill="currentColor" opacity={0.75} d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {children}
        </>
      ) : (
        children
      )}
    </button>
  )
}
